import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import State from '../../context';

import loc from '../../utils/loc';

import Share from '../Share';
import Button from '../Button';
import Image from '../Image';
import ProgressBar from '../progressBar';
import Cta from './Cta';

// Show points in most common fractions instead of decimals
const commonFractions = {
  '50': '½',
  '33': '⅓',
  '66': '⅔',
  '25': '¼',
  '75': '¾'
};

const Result = ({ userResults, quizResults, quizName, quizImage, reset, cta = {}, totalPoints, finalLink, finalLinkText }) => {
  const state = useContext(State);

  let allResults = quizResults.map(result => ({
    ...result,
    userResult: userResults[result.uuid]
  })).sort((a, b) => (a.userResult < b.userResult) ? 1 : -1);

  if(state.mode === 'percents' && state.settings.showBestMatchOnly) {
    allResults = [allResults[0]];
  }

  function renderShare() {
    let shareResult = '';

    if(state.mode === 'points') {
      shareResult = `${Math.floor(allResults[0].userResult)}/${totalPoints}`;
    } else if(state.settings.showBestMatchOnly) {
      shareResult = allResults[0].name;
    } else {
      shareResult = allResults.map(res => `${res.userResult}% ${res.name}`).join(', ');
    }

    const shareMessage = `${loc.t('my results are')} ${shareResult}. ${quizName}`;
    const shareUrl = window.location.href.split('?')[0];

    return <Share message={shareMessage} url={shareUrl} />
  }

  function renderPointsResult() {
    // Points result array only has one item.
    return allResults.map(result => {
      let userPoints = result.userResult;

      const parts = result.userResult.toFixed(2).split('.');

      // Show decimal points with common fractions, e.g. 1½
      // Fractions come from multichoice questions
      if(parts[1] && commonFractions[parts[1]]) {
        userPoints = parts[0] + commonFractions[parts[1]];
      } else {
        userPoints = Math.floor(userPoints);
      }

      // Show different result based on amount of points
      // Round fractional points up
      const roundedPoints = Math.round(result.userResult);
      const pointResult = result.points[roundedPoints];

      return (
        <React.Fragment key={result.uuid}>
          <Title>
            {loc.t('your result')}:
            <br/>
            {userPoints}/{totalPoints}
          </Title>
          <ResultName>{pointResult.name}</ResultName>
          {pointResult.description && <ResultDescription>{pointResult.description}</ResultDescription>}
        </React.Fragment>
      );
    });
  }

  function renderPercentageResults() {
    if(state.settings.showBestMatchOnly) {
      return allResults.map(result => (
        <Section key={result.uuid}>
          <SmallContainer>
            <h3>{result.name}</h3>
            {result.image && <Image url={result.image} alt="" overflow={false} />}
          </SmallContainer>
          <TextContainer>{result.description}</TextContainer>
        </Section>
      ));
    }
    return allResults.map(result => (
      <Section key={result.uuid}>
        <SmallContainer>
          <ResultMeta>
            <span>{result.name}</span>
            <span>{result.userResult}%</span>
          </ResultMeta>
          <ProgressBar percentage={result.userResult} />
          {result.image && <Image url={result.image} alt="" overflow={false} />}
        </SmallContainer>
        <TextContainer>{result.description}</TextContainer>
      </Section>
    ));

  }

  return (
    <Wrapper>
      {state.mode === 'points'
        ? renderPointsResult()
        : <>
          <Title>{loc.t('your result')}:</Title>
          <ResultSet>{renderPercentageResults()}</ResultSet>
        </>
      }

      {state.settings.showSocialMediaShare ? renderShare() : null}

      <Actions>
        {finalLink && finalLinkText ? <Button as="a" primary={true} href={finalLink}>{finalLinkText}</Button> :
          <Button onClick={reset}>{loc.t('do it again')}</Button>}
      </Actions>


      <Cta
        text={cta.text}
        link={cta.link}
        image={cta.image}
      />
    </Wrapper>
  );
};

Result.propTypes = {
  userResults: PropTypes.object,
  quizResults: PropTypes.array,
  quizName: PropTypes.string,
  quizImage: PropTypes.string,
  reset: PropTypes.func,
  cta: PropTypes.shape({
    text: PropTypes.string,
    link: PropTypes.string,
    image: PropTypes.string,
  }),
  totalPoints: PropTypes.number,
  finalLink: PropTypes.string,
  finalLinkText: PropTypes.string
};

const ResultName = styled.h3`
  font-size: 2rem;
  letter-spacing: 3px;
`;

const ResultDescription = styled.p`
  font-size: 1.2rem;
`;

const Wrapper = styled.div`
  text-align: center;
`;

const Title = styled.h2`
  font-size: 1.2rem;
  letter-spacing: 3px;
  margin: 0 0 24px 0;
`;

const ResultSet = styled.div`
  margin-bottom: 40px;
`;

const ResultMeta = styled.div`
  display: flex;
  font-weight: bold;
  justify-content: space-between;
  line-height: normal;
`;

const TextContainer = styled.p`
  max-width: 460px;
  margin-left: auto;
  margin-right: auto;
`;

const SmallContainer = styled.div`
  max-width: 340px;
  margin-left: auto;
  margin-right: auto;
`;

const Actions = styled.div`
  margin-top: 40px;

  > * {
    margin-bottom: 5px;
  }
`;

const Section = styled.div`
  & + & {
    margin-top: 40px;
  }
`;

export default Result;
